import React, { useEffect, useMemo, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import {
  Typography,
  Button,
  Paper,
  Box,
  TextField,
  Divider,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Stack,
  Tooltip,
  IconButton,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useLocation, useNavigate } from "react-router-dom";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { QRCodeSVG } from "qrcode.react";

import { getUserData } from "../../utils/cookieUtils";
import api from "../../services/api";
import { useNotification } from "../../contexts/NotificationContext";
import CustomLoading from "../../components/common/CustomLoading";
import SearchableSelectInput from "../../components/forms/SearchableSelectInput";
import useCodeCommonByCategory from "../../hooks/useCodeCommonByCategory";
const primaryBlue = "rgba(22, 67, 135, 1)";
const hubActionBlue = "rgba(13,44,90,1)";

// getPromoCodeById — typical response fields used when editing.
interface PromoCode {
  couponid: number;
  couponname: string;
  description: string;
  discounttype: number;
  discountvalue: number;
  minbookingamount: number;
  usagelimit: number;
  startdate: string;
  expirydate: string;
  usedcount: number;
  qrcodelink: string;
  userid: number;
  reason: string;
  status: number;
  createdby: number;
  createdon: string;
  modifiedby: number | null;
  modifiedon: string | null;
  isdeleted: boolean;
  is_user_assignable?: boolean;
  is_coupon_reuseable?: boolean;
}

function parsePromoBool(v: unknown, defaultVal: boolean): boolean {
  if (v === true || v === 1) return true;
  if (v === false || v === 0) return false;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    if (s === "true" || s === "1") return true;
    if (s === "false" || s === "0") return false;
  }
  return defaultVal;
}

// Match `SearchableSelectInput` (Autocomplete) small outlined height + padding so row fields line up.
const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 42,
    alignItems: "center",
    "& fieldset": { borderColor: "#dee2e6" },
  },
  "& .MuiInputBase-input": {
    paddingTop: "10px",
    paddingBottom: "10px",
  },
};

const SAVE_PROMO_FALLBACK_ERROR =
  "Failed to save promo code. Please try again.";

const USER_ASSIGNABLE_TOOLTIP =
  "Only users who scan the QR code can use this promo code.";
const COUPON_REUSABLE_TOOLTIP =
  "If enabled, users can use this coupon multiple times.";

const promoInfoTooltipProps = {
  tooltip: {
    sx: {
      bgcolor: "#000",
      color: "#fff",
      fontSize: "0.8125rem",
      maxWidth: 280,
      lineHeight: 1.45,
      px: 1.25,
      py: 1,
      "& .MuiTooltip-arrow": {
        color: "#000",
      },
    },
  },
} as const;

function sanitizeAmountInput(raw: string): string {
  const filtered = raw.replace(/[^\d.]/g, "");
  const firstDot = filtered.indexOf(".");
  if (firstDot === -1) return filtered;
  return (
    filtered.slice(0, firstDot + 1) +
    filtered.slice(firstDot + 1).replace(/\./g, "")
  );
}

function isStrictNonNegativeDecimalString(val: string): boolean {
  const t = val.trim();
  if (t === "" || t === ".") return false;
  if (!/^\d+(\.\d*)?$/.test(t)) return false;
  const n = Number.parseFloat(t);
  return Number.isFinite(n) && n >= 0;
}

// savePromoCode failures: read API `message` from axios response.data (e.g. 409 duplicate name).
function getSavePromoApiErrorMessage(error: unknown): string {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (data && typeof data === "object" && data !== null) {
    const msg = (data as { message?: unknown }).message;
    if (typeof msg === "string" && msg.trim() !== "") return msg.trim();
  }
  if (error instanceof Error && error.message.trim() !== "") {
    return error.message;
  }
  return SAVE_PROMO_FALLBACK_ERROR;
}

const CreatePromoCodePage: React.FC = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const location = useLocation();
  const editId = new URLSearchParams(location.search).get("id");
  // CommonService: CouponDiscountType — codedisplayname / codevalue in UI; codevalue → discounttype on save.
  const { statusCodes: couponDiscountTypeCodes, loading: discountTypeLoading } =
    useCodeCommonByCategory("CouponDiscountType");

  const [codeName, setCodeName] = useState("");
  const [discountType, setDiscountType] = useState("");
  const [discountValue, setDiscountValue] = useState("");
  const [minBooking, setMinBooking] = useState("");
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [usageLimit, setUsageLimit] = useState("");
  const [description, setDescription] = useState("");
  const [isUserAssignable, setIsUserAssignable] = useState(false);
  const [isCouponReuseable, setIsCouponReuseable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchingPromo, setFetchingPromo] = useState(() => Boolean(editId));
  const qrRef = React.useRef<HTMLDivElement | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const qrValue = useMemo(() => codeName.trim(), [codeName]);
  const currentUser = useMemo(() => getUserData(), []);
  const isValidNumber = (val: string) =>
    val.trim() !== "" && !Number.isNaN(Number(val));

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!codeName.trim()) {
      newErrors.codeName = "Code name is required";
    }

    if (!discountType) {
      newErrors.discountType = "Discount type is required";
    }

    if (!discountValue.trim()) {
      newErrors.discountValue = "Discount value is required";
    } else if (!isStrictNonNegativeDecimalString(discountValue)) {
      newErrors.discountValue =
        "Use digits only (optional decimal). Letters and special characters are not allowed.";
    }

    if (!minBooking.trim()) {
      newErrors.minBooking = "Minimum booking amount is required";
    } else if (!isStrictNonNegativeDecimalString(minBooking)) {
      newErrors.minBooking =
        "Use digits only (optional decimal). Letters and special characters are not allowed.";
    }

    const todayStart = dayjs().startOf("day");

    if (!startDate) {
      newErrors.startDate = "Start date is required";
    } else if (startDate.startOf("day").isBefore(todayStart)) {
      newErrors.startDate = "Start date cannot be in the past";
    }

    if (!endDate) {
      newErrors.endDate = "End date is required";
    } else if (!startDate) {
      newErrors.endDate = "Select a start date first";
    } else if (
      !endDate.startOf("day").isAfter(startDate.startOf("day"), "day")
    ) {
      newErrors.endDate = "End date must be after the start date";
    }

    if (!usageLimit.trim()) {
      newErrors.usageLimit = "Usage limit is required";
    } else if (!isValidNumber(usageLimit)) {
      newErrors.usageLimit = "Enter a valid number";
    } else {
      const n = Number.parseInt(usageLimit, 10);
      if (!Number.isFinite(n) || n < 0) {
        newErrors.usageLimit = "Usage limit must be zero or greater";
      }
    }

    if (!description.trim()) {
      newErrors.description = "Description is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // savePromoCode POST body; discounttype from CouponDiscountType codevalue (numeric).
  const buildCreatePayload = () => {
    const trimmed = discountType.trim();
    let discountTypeNum = Number.parseInt(trimmed, 10);
    if (!Number.isFinite(discountTypeNum)) {
      const f = Number.parseFloat(trimmed);
      discountTypeNum = Number.isFinite(f) ? Math.trunc(f) : 0;
    }

    const toNum = (v: string) => {
      const n = Number.parseFloat(v);
      return Number.isFinite(n) ? n : 0;
    };

    const toInt = (v: string) => {
      const n = Number.parseInt(v, 10);
      return Number.isFinite(n) ? n : 0;
    };

    return {
      couponid: 0,
      couponname: codeName.trim(),
      description: description.trim(),
      discounttype: discountTypeNum,
      discountvalue: toNum(discountValue),
      minbookingamount: toNum(minBooking),
      startdate: startDate ? startDate.toISOString() : "",
      expirydate: endDate ? endDate.toISOString() : "",
      usagelimit: toInt(usageLimit),
      qrcodelink: qrValue,
      userid: 0,
      reason: "",
      status: 1,
      is_user_assignable: isUserAssignable,
      is_coupon_reuseable: isCouponReuseable,
    };
  };
  const handleDownloadQR = () => {
    if (!qrRef.current) return;

    const svg = qrRef.current.querySelector("svg");
    if (!svg) return;

    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svg);

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    const img = new Image();
    const blob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx?.drawImage(img, 0, 0);

      URL.revokeObjectURL(url);

      const pngFile = canvas.toDataURL("image/png");

      const downloadLink = document.createElement("a");
      downloadLink.href = pngFile;
      const now = new Date();

      const formattedDateTime = `${now.getFullYear()}-${String(
        now.getMonth() + 1,
      ).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}_${String(
        now.getHours(),
      ).padStart(2, "0")}-${String(now.getMinutes()).padStart(2, "0")}`;

      downloadLink.download = `promocode-${formattedDateTime}.png`;
      downloadLink.click();
    };

    img.src = url;
  };
  useEffect(() => {
    if (!editId) {
      setFetchingPromo(false);
      return;
    }

    let cancelled = false;
    const fetchData = async () => {
      setFetchingPromo(true);
      try {
        // API: getPromoCodeById(editId)
        const res: any = await api.getPromoCodeById(editId);
        const data = res.data as PromoCode;

        if (cancelled) return;
        setCodeName(data.couponname || "");
        setDiscountType(
          data.discounttype !== undefined && data.discounttype !== null
            ? String(data.discounttype)
            : "",
        );
        setDiscountValue(sanitizeAmountInput(String(data.discountvalue ?? "")));
        setMinBooking(sanitizeAmountInput(String(data.minbookingamount ?? "")));
        setUsageLimit(String(data.usagelimit ?? ""));
        setStartDate(data.startdate ? dayjs(data.startdate) : null);
        setEndDate(data.expirydate ? dayjs(data.expirydate) : null);
        setDescription(data.description || "");
        const row = data as PromoCode & Record<string, unknown>;
        setIsUserAssignable(
          parsePromoBool(row.is_user_assignable ?? row.isUserAssignable, false),
        );
        setIsCouponReuseable(
          parsePromoBool(
            row.is_coupon_reuseable ?? row.isCouponReuseable,
            false,
          ),
        );
      } catch (err) {
        console.error("Failed to fetch promo code", err);
      } finally {
        if (!cancelled) setFetchingPromo(false);
      }
    };

    void fetchData();
    return () => {
      cancelled = true;
    };
  }, [editId]);
  const handleCancel = () => {
    navigate("/marketing/promo-codes");
  };

  const handleSave = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      const payload = buildCreatePayload();
      const parsedCouponId =
        editId != null && String(editId).trim() !== ""
          ? Number.parseInt(String(editId), 10)
          : NaN;
      payload.couponid = Number.isFinite(parsedCouponId) ? parsedCouponId : 0;

      // API: savePromoCode (create couponid 0, edit parsed id)
      const res = (await api.savePromoCode(payload)) as {
        isSuccess?: boolean;
        message?: string;
      };

      if (res && res.isSuccess == false) {
        showNotification(
          typeof res.message == "string" && res.message.trim() !== ""
            ? res.message.trim()
            : SAVE_PROMO_FALLBACK_ERROR,
          "error",
        );
        return;
      }
      const defaultMsg = editId
        ? "Promo code updated successfully."
        : "Promo code created successfully.";
      showNotification(
        typeof res?.message === "string" && res.message.trim() !== ""
          ? res.message.trim()
          : defaultMsg,
        "success",
      );
      navigate("/marketing/promo-codes");
    } catch (error: unknown) {
      console.error("Error creating promo code:", error);
      showNotification(getSavePromoApiErrorMessage(error), "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container fluid className="px-0">
        <Paper
          className="border-0 rounded-0 shadow-none"
          sx={{ px: { xs: 2, md: 4 }, py: 3 }}
        >
          {fetchingPromo ? (
            <CustomLoading message="Fetching promo code..." />
          ) : (
            <>
              <Row className="g-3 align-items-start row-cols-1 row-cols-md-2 row-cols-xl-4">
                <Col>
                  <TextField
                    label="Code Name"
                    size="small"
                    fullWidth
                    required
                    value={codeName}
                    onKeyDown={(e) => {
                      if (e.key === " ") {
                        e.preventDefault();
                      }
                    }}
                    onChange={(e) => {
                      setCodeName(e.target.value);
                      setErrors((prev) => ({ ...prev, codeName: "" }));
                    }}
                    error={!!errors.codeName}
                    helperText={errors.codeName}
                    sx={textFieldSx}
                  />
                </Col>

                <Col>
                  <SearchableSelectInput
                    field="discountType"
                    formData={{ discountType: discountType || "" }}
                    onChange={(_field, value) => {
                      setDiscountType(value);
                      setErrors((prev) => ({ ...prev, discountType: "" }));
                    }}
                    label="Discount Type"
                    placeholder="Select discount type"
                    required
                    options={couponDiscountTypeCodes.map((c) => ({
                      label: c.codedisplayname,
                      value: c.codevalue,
                    }))}
                    disabled={fetchingPromo}
                    loading={discountTypeLoading}
                    error={errors.discountType}
                  />
                </Col>

                <Col>
                  <TextField
                    label="Discount Value"
                    size="small"
                    fullWidth
                    required
                    value={discountValue}
                    inputMode="decimal"
                    onChange={(e) => {
                      setDiscountValue(sanitizeAmountInput(e.target.value));
                      setErrors((prev) => ({ ...prev, discountValue: "" }));
                    }}
                    error={!!errors.discountValue}
                    helperText={errors.discountValue}
                    sx={textFieldSx}
                  />
                </Col>

                <Col>
                  <TextField
                    label="Minimum Booking Amount"
                    size="small"
                    fullWidth
                    required
                    value={minBooking}
                    inputMode="decimal"
                    onChange={(e) => {
                      setMinBooking(sanitizeAmountInput(e.target.value));
                      setErrors((prev) => ({ ...prev, minBooking: "" }));
                    }}
                    error={!!errors.minBooking}
                    helperText={errors.minBooking}
                    sx={textFieldSx}
                  />
                </Col>
              </Row>

              <Row className="g-3 align-items-start row-cols-1 row-cols-md-2 row-cols-xl-4 mt-2">
                <Col>
                  <DatePicker
                    label="Start Date"
                    format="DD/MM/YYYY"
                    value={startDate}
                    minDate={dayjs().startOf("day")}
                    onChange={(v) => {
                      setStartDate(v);
                      setEndDate((prev) => {
                        if (
                          v &&
                          prev &&
                          !prev.startOf("day").isAfter(v.startOf("day"), "day")
                        ) {
                          return v.startOf("day").add(1, "day");
                        }
                        return prev;
                      });
                      setErrors((prev) => ({
                        ...prev,
                        startDate: "",
                        endDate: "",
                      }));
                    }}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        required: true,
                        sx: textFieldSx,
                        error: !!errors.startDate,
                        helperText: errors.startDate,
                      },
                    }}
                  />
                </Col>

                <Col>
                  <DatePicker
                    label="End Date"
                    format="DD/MM/YYYY"
                    value={endDate}
                    minDate={
                      startDate
                        ? startDate.startOf("day").add(1, "day")
                        : dayjs().startOf("day").add(1, "day")
                    }
                    onChange={(v) => {
                      setEndDate(v);
                      setErrors((prev) => ({ ...prev, endDate: "" }));
                    }}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        required: true,
                        sx: textFieldSx,
                        error: !!errors.endDate,
                        helperText: errors.endDate,
                      },
                    }}
                  />
                </Col>

                <Col>
                  <TextField
                    label="Usage Limit"
                    size="small"
                    fullWidth
                    required
                    type="number"
                    value={usageLimit}
                    onChange={(e) => {
                      setUsageLimit(e.target.value);
                      setErrors((prev) => ({ ...prev, usageLimit: "" }));
                    }}
                    inputProps={{ min: 0 }}
                    error={!!errors.usageLimit}
                    helperText={errors.usageLimit}
                    sx={textFieldSx}
                  />
                </Col>
                <Col>
                  <TextField
                    label="Description"
                    size="small"
                    fullWidth
                    required
                    type="text"
                    value={description}
                    onChange={(e) => {
                      setDescription(e.target.value);
                      setErrors((prev) => ({ ...prev, description: "" }));
                    }}
                    error={!!errors.description}
                    helperText={errors.description}
                    sx={textFieldSx}
                  />
                </Col>
              </Row>

              <Row className="g-3 mt-2">
                <Col xs={12}>
                  <Stack
                    spacing={1.25}
                    sx={{ width: "100%", alignItems: "flex-start" }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        gap: 0.25,
                        flexWrap: "wrap",
                      }}
                    >
                      <FormControlLabel
                        sx={{ m: 0, mr: 0 }}
                        control={
                          <Checkbox
                            checked={isUserAssignable}
                            onChange={(e) =>
                              setIsUserAssignable(e.target.checked)
                            }
                            size="small"
                            sx={{
                              color: primaryBlue,
                              "&.Mui-checked": { color: primaryBlue },
                            }}
                          />
                        }
                        label={
                          <Typography variant="body2">
                            User assignable
                          </Typography>
                        }
                      />
                      <Tooltip
                        title={USER_ASSIGNABLE_TOOLTIP}
                        arrow
                        placement="top"
                        componentsProps={promoInfoTooltipProps}
                      >
                        <IconButton
                          size="small"
                          aria-label="User assignable information"
                          sx={{ color: primaryBlue, flexShrink: 0, p: 0.25 }}
                        >
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        gap: 0.25,
                        flexWrap: "wrap",
                      }}
                    >
                      <FormControlLabel
                        sx={{ m: 0, mr: 0 }}
                        control={
                          <Checkbox
                            checked={isCouponReuseable}
                            onChange={(e) =>
                              setIsCouponReuseable(e.target.checked)
                            }
                            size="small"
                            sx={{
                              color: primaryBlue,
                              "&.Mui-checked": { color: primaryBlue },
                            }}
                          />
                        }
                        label={
                          <Typography variant="body2">
                            Coupon reusable
                          </Typography>
                        }
                      />
                      <Tooltip
                        title={COUPON_REUSABLE_TOOLTIP}
                        arrow
                        placement="top"
                        componentsProps={promoInfoTooltipProps}
                      >
                        <IconButton
                          size="small"
                          aria-label="Coupon reusable information"
                          sx={{ color: primaryBlue, flexShrink: 0, p: 0.25 }}
                        >
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Stack>
                </Col>
              </Row>

              <Divider sx={{ my: 4 }} />
              {qrValue && (
                <>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: primaryBlue,
                      fontWeight: 600,
                      mb: 2,
                    }}
                  >
                    QR Preview
                  </Typography>

                  <Paper
                    variant="outlined"
                    sx={{
                      maxWidth: 280,
                      p: 3,
                      borderRadius: 2,
                      borderColor: "#d0d0d0",
                      backgroundColor: "#fafafa",
                    }}
                  >
                    <Box
                      ref={qrRef}
                      sx={{
                        position: "relative",
                        aspectRatio: "1",
                        maxWidth: 200,
                        mx: "auto",
                        border: "1px dashed #bdbdbd",
                        borderRadius: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#fff",
                        overflow: "hidden",
                        "&:hover .qr-download-btn": {
                          opacity: 1,
                        },
                      }}
                    >
                      <QRCodeSVG
                        value={qrValue}
                        size={180}
                        includeMargin={false}
                      />

                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleDownloadQR}
                        className="qr-download-btn"
                        sx={{
                          position: "absolute",
                          bottom: 8,
                          right: 8,
                          fontSize: "11px",
                          px: 1.5,
                          py: 0.5,
                          minWidth: "auto",
                          backgroundColor: "rgba(0,0,0,0.7)",
                          opacity: 0,
                          transition: "0.3s",
                          "&:hover": {
                            backgroundColor: "rgba(0,0,0,0.85)",
                          },
                        }}
                      >
                        Download
                      </Button>
                    </Box>

                    <Typography
                      variant="body2"
                      align="center"
                      sx={{ mt: 2, color: "text.secondary" }}
                    >
                      QR is generated from code name
                    </Typography>
                  </Paper>
                </>
              )}
            </>
          )}
        </Paper>
      </Container>

      <Box className="d-flex justify-content-end gap-3 mt-4">
        <Button
          variant="contained"
          className="px-2 py-2 fw-medium text-white"
          onClick={() => void handleSave()}
          disabled={loading || fetchingPromo}
          sx={{
            backgroundColor: hubActionBlue,
            minWidth: "130px",
            "&:disabled": {
              backgroundColor: "rgba(13,44,90,0.5)",
            },
          }}
        >
          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={16} color="inherit" />
              Saving...
            </Box>
          ) : (
            "Save"
          )}
        </Button>

        <Button
          variant="outlined"
          className="px-4 py-2 fw-medium"
          onClick={handleCancel}
          disabled={loading || fetchingPromo}
          sx={{
            borderColor: hubActionBlue,
            color: hubActionBlue,
            "&:hover": {
              borderColor: hubActionBlue,
              backgroundColor: "rgba(13,44,90,0.04)",
            },
          }}
        >
          Cancel
        </Button>
      </Box>
    </LocalizationProvider>
  );
};

export default CreatePromoCodePage;
